import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

interface Props {
  open: boolean;
  title: string;
  description?: string;
  placeholder?: string;
  confirmLabel?: string;
  tone?: "primary" | "danger";
  // Returning nothing cancels; the note is trimmed before it is handed over.
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

// In-app replacement for window.prompt — a browser prompt looks like a phishing
// dialog, cannot be styled and is easy to dismiss by accident. This one names
// the project context, requires a real answer, and matches the rest of CRE.
export default function NoteDialog({
  open,
  title,
  description,
  placeholder,
  confirmLabel = "Submit",
  tone = "primary",
  onConfirm,
  onCancel,
}: Props) {
  const [note, setNote] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setNote("");
      // Focus after the dialog paints so typing starts immediately.
      setTimeout(() => ref.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const submit = () => {
    if (!note.trim()) return;
    onConfirm(note.trim());
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
      onClick={onCancel}
      onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-[15px] font-bold text-gray-900">{title}</h3>
            {description && <p className="text-[12px] text-gray-500 mt-0.5 leading-relaxed">{description}</p>}
          </div>
          <button onClick={onCancel} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 shrink-0">
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4">
          <textarea
            ref={ref}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit();
            }}
            rows={4}
            placeholder={placeholder}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
          />
          <p className="text-[11px] text-gray-400 mt-1.5">This note is sent to the Project Manager in ERP.</p>
        </div>

        <div className="px-5 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-white"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!note.trim()}
            className={`px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40 ${
              tone === "danger" ? "bg-red-600 hover:brightness-110" : "bg-primary hover:brightness-110"
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
